import { Content } from '@amzn/awscat-pattern-library-service-model';
import type { TiptapOutputType } from '@amzn/awscat-editor';
import { deepCloneContent } from '../../model/CloneContent';
import { enumeratePages } from '../PageOperations';

// Namespaced so a page id can't collide with a section id in the same map.
// Section keys are scoped by page id because section ids are only unique within a page.
const pageKey = (id: string): string => `page_${id}`;
const sectionKey = (pageId: string, sectionId: string): string => `section_${pageId}_${sectionId}`;

/**
 * Collects per-page and per-section body replacements for a Guide, then
 * produces a cloned Content with those replacements applied in one pass.
 */
export class ContentPatcher {
  private readonly patchedSections = new Map<string, TiptapOutputType>();

  /** Number of patches queued so far. */
  get size(): number {
    return this.patchedSections.size;
  }

  /** Queue a replacement for the given page's body. */
  patchPage(pageId: string, contentData: TiptapOutputType): void {
    this.patchedSections.set(pageKey(pageId), contentData);
  }

  /** Queue a replacement for the given section's body within a page. */
  patchSection(pageId: string, sectionId: string, contentData: TiptapOutputType): void {
    this.patchedSections.set(sectionKey(pageId, sectionId), contentData);
  }

  /** Returns a deep clone of the provided Content with all queued patches applied. */
  getPatched(content: Content): Content {
    const merged = deepCloneContent(content);
    for (const page of enumeratePages(merged)) {
      const pKey = pageKey(page.id);
      if (this.patchedSections.has(pKey)) {
        page.contentData = this.patchedSections.get(pKey);
      }
      for (const section of page.contentSections ?? []) {
        const sKey = sectionKey(page.id, section.id);
        if (this.patchedSections.has(sKey)) {
          section.contentData = this.patchedSections.get(sKey);
        }
      }
    }
    return merged;
  }
}
